import React, { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../../constants/property';

const MAINTENANCE_URL = `${API_BASE_URL}/api/maintence`;

const ListTable = ({
  rows,
  loading,
  listError,
  emptyHint,
  onRefresh,
  onDelete,
  deletingId,
  listHeading,
}) => (
  <div className="lg:col-span-8 min-w-0 rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h3 className="font-medium text-gray-900">{listHeading}</h3>
      <button
        type="button"
        onClick={onRefresh}
        disabled={loading}
        className="px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-60"
      >
        {loading ? 'Loading…' : 'Refresh'}
      </button>
    </div>

    {listError && (
      <p className="text-sm text-red-600" role="alert">
        {listError}
      </p>
    )}

    {loading && rows.length === 0 && !listError ? (
      <p className="text-sm text-gray-500">Loading…</p>
    ) : rows.length === 0 ? (
      <p className="text-sm text-gray-500">{emptyHint}</p>
    ) : (
      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Name</th>
              <th className="px-4 py-2 text-left font-medium text-gray-700">Code</th>
              <th className="px-4 py-2 text-right font-medium text-gray-700 w-28">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {rows.map((row) => (
              <tr key={row.id ?? `${row.value}-${row.code}`}>
                <td className="px-4 py-2 text-gray-900">{row.value ?? '—'}</td>
                <td className="px-4 py-2 text-gray-700">{row.code ?? '—'}</td>
                <td className="px-4 py-2 text-right">
                  <button
                    type="button"
                    onClick={() => onDelete(row)}
                    disabled={deletingId === row.id}
                    className="text-sm text-red-600 hover:text-red-800 disabled:opacity-50"
                  >
                    {deletingId === row.id ? 'Deleting…' : 'Delete'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

/**
 * Single maintenance type: POST/GET/DELETE via /api/maintence (same pattern as Vendor).
 * @param {{ apiType: string, addTitle: string, valueLabel: string, valuePlaceholder: string, codePlaceholder: string, listHeading: string, emptyHint: string, deleteModalLabel: string }} props
 */
export function MaintenanceEntitySection({
  apiType,
  addTitle,
  valueLabel,
  valuePlaceholder,
  codePlaceholder,
  listHeading,
  emptyHint,
  deleteModalLabel,
}) {
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');

  const [rows, setRows] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState('');

  const [deletingId, setDeletingId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const fetchList = useCallback(async () => {
    setListError('');
    setLoadingList(true);
    try {
      const url = `${API_BASE_URL}/api/maintence/${apiType}`;
      const response = await fetch(url, { method: 'GET', mode: 'cors' });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
      }
      const data = await response.json();
      const list = Array.isArray(data) ? data : [];
      const filtered = list.filter((r) => !r.type || String(r.type).toLowerCase() === apiType.toLowerCase());
      setRows(filtered);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`${apiType} list error:`, err);
      setListError(err.message || `Failed to load ${apiType} list.`);
      setRows([]);
    } finally {
      setLoadingList(false);
    }
  }, [apiType]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const onSubmit = async (e) => {
    e.preventDefault();
    const v = value.trim();
    const c = code.trim() || v;
    setFormError('');
    if (!v) {
      setFormError(`${valueLabel} is required.`);
      return;
    }
    setSaving(true);
    try {
      const response = await fetch(MAINTENANCE_URL, {
        method: 'POST',
        mode: 'cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: apiType, value: v, code: c }),
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
      }
      alert(`${apiType} added successfully.`);
      setValue('');
      setCode('');
      await fetchList();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(`Add ${apiType} error:`, err);
      setFormError(err.message || `Failed to add ${apiType}.`);
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete || pendingDelete.id == null) {
      setPendingDelete(null);
      return;
    }
    const row = pendingDelete;
    setDeletingId(row.id);
    try {
      const response = await fetch(`${MAINTENANCE_URL}/id/${row.id}`, {
        method: 'DELETE',
        mode: 'cors',
      });
      if (!response.ok) {
        const errText = await response.text().catch(() => '');
        throw new Error(errText || `HTTP ${response.status}: ${response.statusText}`);
      }
      await fetchList();
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Delete error:', err);
      alert(`Failed to delete: ${err.message || 'Unknown error'}`);
    } finally {
      setDeletingId(null);
      setPendingDelete(null);
    }
  };

  const idForValue = `maint-${apiType}-value`.toLowerCase();
  const idForCode = `maint-${apiType}-code`.toLowerCase();

  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
        <form
          onSubmit={onSubmit}
          className="lg:col-span-4 rounded-lg border border-gray-200 bg-white p-6 shadow-sm space-y-4"
        >
          <h4 className="font-medium text-gray-900">{addTitle}</h4>
          <div className="space-y-2">
            <label htmlFor={idForValue} className="block text-sm font-medium text-gray-700">
              {valueLabel} <span className="text-red-500">*</span>
            </label>
            <input
              id={idForValue}
              type="text"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setFormError('');
              }}
              placeholder={valuePlaceholder}
              autoComplete="off"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          <div className="space-y-2">
            <label htmlFor={idForCode} className="block text-sm font-medium text-gray-700">
              Code
            </label>
            <input
              id={idForCode}
              type="text"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
                setFormError('');
              }}
              placeholder={codePlaceholder}
              autoComplete="off"
              className="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
          {/* <div className="rounded-md bg-gray-50 border border-gray-200 p-4 text-xs font-mono whitespace-pre-wrap break-words">
            {JSON.stringify(
              {
                type: apiType,
                value: value.trim() || '(required)',
                code: code.trim() || value.trim() || '(required)',
              },
              null,
              2
            )}
          </div> */}
          {formError && (
            <p className="text-sm text-red-600" role="alert">
              {formError}
            </p>
          )}
          <button
            type="submit"
            disabled={saving}
            className={`px-4 py-2 rounded-md text-white text-sm font-medium ${
              saving ? 'bg-teal-600/70 cursor-not-allowed' : 'hover:opacity-90'
            }`}
            style={{ backgroundColor: '#009688' }}
          >
            {saving ? 'Saving...' : `Add`}
          </button>
        </form>

        <ListTable
          rows={rows}
          loading={loadingList}
          listError={listError}
          emptyHint={emptyHint}
          onRefresh={fetchList}
          onDelete={(row) => setPendingDelete(row)}
          deletingId={deletingId}
          listHeading={listHeading}
        />
      </div>

      {pendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
            onClick={() => setPendingDelete(null)}
            aria-hidden="true"
          />
          <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-2">Delete {deleteModalLabel}</h3>
            <p className="text-sm text-gray-600 mb-6">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-gray-900">
                &quot;{pendingDelete.value ?? pendingDelete.code}&quot;
              </span>
              ? This action cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setPendingDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={deletingId === pendingDelete.id}
                className="px-4 py-2 text-sm font-medium text-white rounded-md bg-red-600 hover:bg-red-700 disabled:opacity-60"
              >
                {deletingId === pendingDelete.id ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
